import { createTheme } from "@mui/material/styles";

// Extend the Theme interface for custom colors
declare module "@mui/material/styles" {
  interface Palette {
    tertiary: Palette["primary"];
  }
  interface PaletteOptions {
    tertiary?: PaletteOptions["primary"];
  }
}

// HappyPaws Custom Theme
const theme = createTheme({
  palette: {
    primary: {
      main: "#178B8E", // Teal - HappyPaws brand color
      light: "#4FADAF",
      dark: "#0F6164",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#FF6B35", // Orange accent
      light: "#FF8A5C",
      dark: "#E65428",
      contrastText: "#FFFFFF",
    },
    tertiary: {
      main: "#FDB833", // Yellow accent for CTAs
      light: "#FDC85C",
      dark: "#E0A42E",
      contrastText: "#1A1A1A",
    },
    background: {
      default: "#FAF8F5", // Warm off-white background
      paper: "#FFFFFF",
    },
    text: {
      primary: "#1A1A1A",
      secondary: "#5A5A5A",
    },
    error: {
      main: "#D32F2F",
    },
    warning: {
      main: "#FFA726",
    },
    success: {
      main: "#66BB6A",
    },
  },

  typography: {
    fontFamily: '"Montserrat", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',

    h1: {
      fontWeight: 700,
      fontSize: "3rem", // 48px
      lineHeight: 1.2,
      color: "#178B8E",
      "@media (max-width:600px)": {
        fontSize: "2.5rem", // 40px on mobile
      },
    },
    h2: {
      fontWeight: 700,
      fontSize: "2.5rem", // 40px
      lineHeight: 1.3,
      color: "#178B8E",
      "@media (max-width:600px)": {
        fontSize: "2rem", // 32px on mobile
      },
    },
    h3: {
      fontWeight: 600,
      fontSize: "2rem", // 32px
      lineHeight: 1.4,
      color: "#178B8E",
      "@media (max-width:600px)": {
        fontSize: "1.75rem", // 28px on mobile
      },
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.5rem", // 24px
      lineHeight: 1.4,
      color: "#1A1A1A",
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.25rem", // 20px
      lineHeight: 1.5,
      color: "#1A1A1A",
    },
    h6: {
      fontWeight: 600,
      fontSize: "1rem", // 16px
      lineHeight: 1.5,
      color: "#1A1A1A",
    },
    body1: {
      fontSize: "1rem", // 16px
      lineHeight: 1.6,
      color: "#5A5A5A",
    },
    body2: {
      fontSize: "0.875rem", // 14px
      lineHeight: 1.6,
      color: "#5A5A5A",
    },
    button: {
      textTransform: "none", // Keep normal case for buttons
      fontWeight: 600,
      fontSize: "1rem",
    },
  },

  shape: {
    borderRadius: 12, // Global border radius
  },

  spacing: 8, // Base spacing unit (8px)

  components: {
    // Button customization
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 25, // Rounded pill-shaped buttons
          padding: "10px 28px",
          fontSize: "1rem",
          fontWeight: 600,
          boxShadow: "none",
          transition: "all 0.2s ease",
          "&:hover": {
            boxShadow: "none",
            transform: "translateY(-2px)",
          },
        },
        contained: {
          "&:hover": {
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          },
        },
        outlined: {
          borderWidth: 2,
          "&:hover": {
            borderWidth: 2,
          },
        },
        sizeLarge: {
          padding: "12px 36px",
          fontSize: "1.1rem",
        },
        sizeSmall: {
          padding: "6px 20px",
          fontSize: "0.875rem",
        },
      },
    },

    // TextField customization
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 12,
            backgroundColor: "#F5F5F5",
            transition: "all 0.2s ease",
            "& fieldset": {
              borderColor: "transparent",
            },
            "&:hover": {
              backgroundColor: "#ECECEC",
              "& fieldset": {
                borderColor: "#178B8E",
              },
            },
            "&.Mui-focused": {
              backgroundColor: "#FFFFFF",
              "& fieldset": {
                borderColor: "#178B8E",
                borderWidth: 2,
              },
            },
            "&.Mui-error fieldset": {
              borderColor: "#D32F2F",
            },
          },
        },
      },
    },

    // Card customization
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
          },
        },
      },
    },

    // AppBar customization
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
          backgroundColor: "#FFFFFF",
        },
      },
    },

    // Chip customization
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },

    // Paper customization
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
        },
        elevation2: {
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        },
      },
    },

    // Dialog customization
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },

    // Checkbox customization
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: "#178B8E",
          "&.Mui-checked": {
            color: "#178B8E",
          },
        },
      },
    },

    // Radio customization
    MuiRadio: {
      styleOverrides: {
        root: {
          color: "#178B8E",
          "&.Mui-checked": {
            color: "#178B8E",
          },
        },
      },
    },

    // Switch customization
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": {
            color: "#178B8E",
            "& + .MuiSwitch-track": {
              backgroundColor: "#178B8E",
            },
          },
        },
      },
    },
  },

  // Breakpoints (default MUI values, but included for reference)
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

export default theme;
